import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { PyEntityCreationForm } from '../PyEntityCreationForm';
import { useTranslation } from 'react-i18next';
import { validateField, normalizeWebsiteUrl, isFormValid, mapToRequest } from './contactForm';
import type { CreateContactRequest } from 'services/company-and-contacts/entities';

export type { ContactFormData } from 'services/entities/ContactEntity';

export const initialFormValuesDeprecated: CreateContactRequest = {
  companies: [],
  firstName: '',
  lastName: '',
  email: '',
};

const CreateContactFormDeprecated = ({
  onValidityChange,
  onFormDataChange,
  initialValues = initialFormValuesDeprecated,
  existingEmails = [],
}: {
  onValidityChange?: (isValid: boolean) => void;
  onFormDataChange?: (data: CreateContactRequest) => void;
  initialValues?: CreateContactRequest;
  existingEmails?: string[];
}) => {
  const { t } = useTranslation();
  const {
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors, touchedFields },
  } = useForm<Record<string, string>>({
    defaultValues: {
      firstName: initialValues.firstName ?? '',
      lastName: initialValues.lastName ?? '',
      email: initialValues.email ?? '',
      title: initialValues.title ?? '',
      mobileNo: initialValues.mobileNo ?? '',
      officeNo: initialValues.officeNo ?? '',
      websiteUrl: initialValues.websiteUrl ?? '',
    },
  });

  const formValues = watch();

  const applyValidation = (field: string, value: string) => {
    const { error } = validateField(field, value, t, existingEmails);
    error ? setError(field, { type: 'validate', message: error }) : clearErrors(field);
    return error;
  };

  const notifyParent = (validatedField?: string, fieldError?: string) => {
    const currentValues = getValues();
    const hasErrors = validatedField
      ? !!fieldError || Object.keys(errors).filter((k) => k !== validatedField).length > 0
      : Object.keys(errors).length > 0;
    onValidityChange?.(isFormValid(currentValues, hasErrors));
    onFormDataChange?.(mapToRequest(currentValues, initialValues.companies));
  };

  const handleBlur = (field: string, value: string) => {
    const nextValue = field === 'websiteUrl' ? normalizeWebsiteUrl(value) : value;
    setValue(field, nextValue, { shouldTouch: true });
    const error = applyValidation(field, nextValue);
    notifyParent(field, error);
  };

  const handleInputChange = (field: string, value: string) => {
    setValue(field, value);
    if (touchedFields[field]) {
      const error = applyValidation(field, value);
      notifyParent(field, error);
    } else {
      notifyParent();
    }
  };

  const formFields = [
    {
      id: 'firstName',
      label: 'First Name',
      type: 'text' as const,
      placeholder: 'Enter first name',
      required: true,
      error: !!errors['firstName'],
      helperText: errors['firstName']?.message || '',
    },
    {
      id: 'title',
      label: 'Title',
      type: 'text' as const,
      placeholder: 'Enter job title',
      error: !!errors['title'],
      helperText: errors['title']?.message || '',
    },
    {
      id: 'mobileNo',
      label: 'Mobile Number',
      type: 'text' as const,
      placeholder: 'Enter mobile number',
      error: !!errors['mobileNo'],
      helperText: errors['mobileNo']?.message || '',
    },
    {
      id: 'lastName',
      label: 'Last Name',
      type: 'text' as const,
      placeholder: 'Enter last name',
      required: true,
      error: !!errors['lastName'],
      helperText: errors['lastName']?.message || '',
    },
    {
      id: 'email',
      label: 'Email',
      type: 'text' as const,
      placeholder: 'Enter email address',
      required: true,
      error: !!errors['email'],
      helperText: errors['email']?.message || '',
    },
    {
      id: 'officeNo',
      label: 'Office Number',
      type: 'text' as const,
      placeholder: 'Enter office number',
      error: !!errors['officeNo'],
      helperText: errors['officeNo']?.message || '',
    },
    {
      id: 'websiteUrl',
      label: 'Website URL',
      type: 'text' as const,
      placeholder: 'Enter website URL',
      error: !!errors['websiteUrl'],
      helperText: errors['websiteUrl']?.message || '',
    },
  ];

  const formInitialValues = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.id] = (formValues[field.id as keyof typeof formValues] as string | undefined) ?? '';
      return acc;
    }, {} as Record<string, string>);
  }, [formFields, formValues]);

  return (
    <PyEntityCreationForm
      formFields={formFields}
      initialValues={formInitialValues}
      onFormDataChange={(newValues) => {
        Object.entries(newValues).forEach(([field, value]) => {
          if (typeof value === 'string') {
            handleInputChange(field, value);
          }
        });
      }}
      textInputValidation={{
        firstName: { onBlur: (value) => handleBlur('firstName', value) },
        title: { onBlur: (value) => handleBlur('title', value) },
        lastName: { onBlur: (value) => handleBlur('lastName', value) },
        email: { onBlur: (value) => handleBlur('email', value) },
        mobileNo: { onBlur: (value) => handleBlur('mobileNo', value) },
        officeNo: { onBlur: (value) => handleBlur('officeNo', value) },
        websiteUrl: { onBlur: (value) => handleBlur('websiteUrl', value) },
      }}
    />
  );
};

export default CreateContactFormDeprecated;
